import React, { useEffect, useState } from "react";
import ViewTable from "../../../Components/Common/ViewTable";
import { get, post, put, del, api } from "../../../utils/api";
import Cards from "../../../Components/Common/Cards";
import FilterModal from "../../../Components/Common/FilterModal";
import ActionButtons from "../../../Components/Common/ActionButtons";
import ViewData from "./ViewData";
import Form from "./Form";
import { Alert, Snackbar } from "@mui/material";
import BulkUploadModal from "../../../Components/Common/BulkUploadModal";
import DeleteAlertDialog from "../../../Components/Masters/DeleteAlertDialog";

const FuelEnergyActivities = () => {
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [tableData, setTableData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [filterObj, setFilterObj] = useState({});
  const [filters, setFilters] = useState({
    capitalGoodsCategory: [],
    capitalGoodName: [],
  });
  const [deletedItemId, setDeletedItemId] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [bulkUploadModal, setBulkUploadModal] = useState(false);
  const [excelData, setExcelData] = useState(null);

  const [cardData, setCardData] = useState({
    totalEntries: 0,
    totalQuantityPurchased: 0,
    totalUsefulLifeYears: 0,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [snackOpen, setsnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const headers = [
    { key: "createdAt", label: "Created At", sortable: true },
    { key: "date", label: "Date", sortable: true },
    { key: "capitalGoodName", label: "Name of Capital Good", sortable: true },
    { key: "quantityPurchased", label: "Quantity Purchased", sortable: true },
    { key: "usefulLifeYears", label: "Useful Life (Years)", sortable: true },
  ];

  const inputData = [
    { fieldName: "date", title: "Date" },
    { fieldName: "capitalGoodsCategory", title: "Capital Goods Category" },
    { fieldName: "capitalGoodName", title: "Name of the Capital Good" },
    { fieldName: "quantityPurchased", title: "Quantity Purchased" },
    { fieldName: "usefulLifeYears", title: "Useful Life (Years)" },
    {
      fieldName: "fuelConsumptionForEnergyGeneration",
      title: "Fuel consumption for energy generation",
    },
    {
      fieldName: "upstreamEmissionsFromElectricityGeneration",
      title: "Upstream emissions from electricity generation",
    },
    {
      fieldName: "fuelConsumptionForTransportation",
      title: "Fuel consumption for transportation",
    },
    {
      fieldName: "tAndDLossesFromElectricityPurchased",
      title: "T&D losses from electricity purchased",
    },
    {
      fieldName: "generationOfPurchasedElectricitySoldToEndUsers",
      title: "Generation of Purchased Electricity Sold to End Users",
    },
    {
      fieldName: "energyUsedForUpstreamProcesses",
      title: "Energy used for upstream processes",
    },
  ];

  const filterData = [
    { title: "Date", type: "date", key: "date", value: [] },
    {
      title: "Capital Goods Category",
      type: "option",
      key: "capitalGoodsCategory",
      value: filters?.capitalGoodsCategory || [],
    },
    {
      title: "Name of Capital Good",
      type: "option",
      key: "capitalGoodName",
      value: filters?.capitalGoodName || [],
    },
  ];

  const fetchData = async () => {
    try {
      const filter = { ...filterObj };

      if (filter.date) {
        filter.startDate = filter?.date?.start;
        filter.endDate = filter?.date?.end || new Date();
      }

      const queryParams = new URLSearchParams({
        ...filter,
        page: currentPage,
        limit: 10,
        sortField: sortConfig.key,
        sortOrder: sortConfig.direction,
      }).toString();

      const res = await get(`/scope3/fuelEnergyActivities/get?${queryParams}`);

      const arr =
        res?.data?.data?.map((item) => ({
          date: item?.date,
          capitalGoodName: item?.capitalGoodName,
          quantityPurchased: item?.quantityPurchased,
          usefulLifeYears: item?.usefulLifeYears,
          action: "View",
          id: item?._id,
          ...item,
        })) || [];

      setTableData(arr);
      setTotalPages(res?.data?.totalPages || 1);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [sortConfig, filterObj, currentPage]);

  const fetchCardData = async () => {
    try {
      const res = await get(`/scope3/fuelEnergyActivities/cardData`);
      const data = res?.data?.data || {};
      setCardData({
        totalEntries: data?.totalEntries || 0,
        totalQuantityPurchased: data?.totalQuantityPurchased || 0,
        totalUsefulLifeYears: data?.totalUsefulLifeYears || 0,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchFilters = async () => {
    try {
      const res = await get(`/scope3/fuelEnergyActivities/filter`);
      const { capitalGoodsCategory, capitalGoodName } = res?.data?.data || {};

      setFilters({
        capitalGoodsCategory: capitalGoodsCategory || [],
        capitalGoodName: capitalGoodName || [],
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCardData();
  }, []);

  useEffect(() => {
    fetchExcelData();
  }, []);

  useEffect(() => {
    fetchFilters();
  }, []);

  const bannerData = [
    {
      title: "Total Entries",
      description: `${cardData?.totalEntries ?? 0}`,
    },
    {
      title: "Total Quantity Purchased",
      description: `${cardData?.totalQuantityPurchased ?? 0}`,
    },
    {
      title: "Total Useful Life Years",
      description: `${cardData?.totalUsefulLifeYears ?? 0}`,
    },
  ];

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleFilterChange = (newFilterObj) => {
    setFilterObj(newFilterObj);
    setCurrentPage(1);
  };

  const fetchExcelData = () => {
    const capitalGoodsCategories = [
      "Production Materials",
      "Finished Goods",
      "Transportation & Distribution",
      "IT & Communication",
      "Professional Services",
      "Other",
    ];

    let arr = [];
    let serialNo = 1;

    // Generate a template row for each Capital Goods Category
    capitalGoodsCategories.forEach((category) => {
      arr.push({
        "S No.": serialNo++,
        "Date (DD/MM/YYYY)": "",
        "Capital Goods Category": category,
        "Name of the Capital Good": "",
        "Quantity Purchased": "",
        "Useful Life (Years)": "",
        "Fuel consumption for energy generation": "",
        "Upstream emissions from electricity generation": "",
        "Fuel consumption for transportation": "",
        "T&D losses from electricity purchased": "",
        "Generation of Purchased Electricity Sold to End Users": "",
        "Energy used for upstream processes": "",
      });
    });

    setExcelData(arr);
  };

  const openModal = (type, dataForEdit) => {
    if (type === "add") {
      setIsModalOpen(true);
      setEditModal(false);
      setEditData({});
    } else if (type === "edit") {
      setIsModalOpen(true);
      setEditData(dataForEdit);
      setEditModal(true);
      setViewModal(false);
    }
  };

  const handleEdit = (data) => {
    openModal("edit", data);
  };

  const openViewModal = (data) => {
    setEditData(data);
    setViewModal(true);
  };

  const closeModal = (type) => {
    if (type === "add") {
      setIsModalOpen(false);
    } else if (type === "edit") {
      setIsModalOpen(false);
      setEditModal(false);
      setEditData({});
    }
  };

  const handleSubmit = async (data, isEditing) => {
    try {
      const url = isEditing
        ? `/scope3/fuelEnergyActivities/update?Id=${editData.id || editData._id}`
        : `/scope3/fuelEnergyActivities/add`;
  
      const apiCall = isEditing ? put : post;
  
      const res = await apiCall(url, data);
  
      if (res) {
        setsnackOpen({
          open: true,
          message: res?.data?.message,
          severity: "success",
        });
  
        setIsModalOpen(false);
  
        if (isEditing) {
          setEditModal(false);
          setEditData({});
        }
  
        fetchCardData();
        fetchData();
      }
    } catch (error) {
      setsnackOpen({
        open: true,
        message: error?.response?.data?.message || "Something went wrong",
        severity: "error",
      });
  
      console.error("Error in handleSubmit:", error);
    }
  };

  const handleDeleteOpen = (row) => {
    setDeletedItemId(row);
    setDeleteDialog(true);
  };



  return (
    <>
      <Cards
        data={bannerData}
        title={
          "Fuel- and Energy-Related Activities Not Included in Scope 1 or Scope 2:"
        }
      />

      <ActionButtons
        onFilterClick={() => setIsFilterModalOpen(true)}
        onAddClick={() => openModal("add")}
        filterObj={filterObj}
        onUploadClick={() => setBulkUploadModal(true)}
      />

      <ViewTable
        title={
          "Fuel- and Energy-Related Activities Not Included in Scope 1 or Scope 2:"
        }
        headers={headers}
        data={tableData ?? []}
        onSort={handleSort}
        sortConfig={sortConfig}
        page={currentPage}
        count={10}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onView={(row) => {
          openViewModal(row);
        }}
        onEdit={(row) => openModal("edit", row)}
        onDelete={(row) => handleDeleteOpen(row)}
      />

      <Snackbar
        open={snackOpen.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setsnackOpen({ ...snackOpen, open: false })}
      >
        <Alert
          onClose={() => setsnackOpen({ ...snackOpen, open: false })}
          severity={snackOpen.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackOpen.message}
        </Alert>
      </Snackbar>

      <Form
        isOpen={isModalOpen}
        onClose={() => closeModal(editModal ? "edit" : "add")}
        onSubmit={handleSubmit}
        header={
          editModal
            ? "Edit Fuel- and Energy-Related Activity"
            : "Add Fuel- and Energy-Related Activity"
        }
        initialData={editData}
        isEditing={editModal}
      />

      <ViewData
        id={editData ? editData.id || editData._id : ""}
        element={editData}
        title={"Fuel and energy"}
        viewData={viewModal}
        setViewData={setViewModal}
        setData={setEditData}
        inputData={inputData}
        handleEdit={handleEdit}
      />

      {deleteDialog && (
        <DeleteAlertDialog
          {...{
            deleteDialog,
            setDeleteDialog,
            method: "delete",
            id: deletedItemId?.id || deletedItemId?._id,
            url: `${api}/scope3/fuelEnergyActivities/delete`,
            setsnackOpen,
            fetchData,
            fetchCardData,
          }}
        />
      )}

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filterData}
        onFilterChange={handleFilterChange}
        title="Filter"
      />

      {bulkUploadModal && (
        <BulkUploadModal
          onCloseBulkUpload={() => {
            setBulkUploadModal(false);
            fetchData();
            fetchCardData();
          }}
          isBulkUploadModal={bulkUploadModal}
          excelData={excelData}
          fileName={"fuelEnergyActivities"}
          setsnackOpen={setsnackOpen}
          url={"/scope3/fuelEnergyActivities/bulkupload"}
        />
      )}
    </>
  );
};

export default FuelEnergyActivities;

